/**
 * Request log.
 *
 * The list a customer reaches for when a screenshot did not do what they expected, so it is built
 * around answering "which request, and what happened to it" rather than around looking busy.
 *
 * Two behaviours are deliberate and easy to get wrong. Changing a filter clears the cursor, because
 * a cursor describes a position in the *previous* filter's ordering and reusing it would silently
 * skip rows. And the previous page stays on screen while the next one loads, so paging does not
 * blank the table and move the content under the reader.
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useApiKeys, useRequestLogs, type RequestLogSummary } from '../../lib/api';
import { Button, Card, StatusBadge } from '../../components/ui';

const PAGE_SIZE = 25;

/**
 * Filters excluding pagination, which is what a change must reset the cursor for.
 *
 * Declared explicitly rather than as `Omit<RequestLogFilters, …>`: that type carries an index
 * signature so the request helper can build a query string, and inheriting it would widen every
 * field to `string | number` here.
 */
interface Filters {
  status?: string;
  cacheStatus?: string;
  keyId?: string;
  from?: string;
  to?: string;
}

function formatTimestamp(value: string): string {
  return new Date(value).toLocaleString();
}

function formatDuration(ms: number): string {
  return ms < 1000 ? `${String(ms)} ms` : `${(ms / 1000).toFixed(2)} s`;
}

export function RequestLogsRoute() {
  const [filters, setFilters] = useState<Filters>({});
  // Every cursor visited so far. Keeping the stack lets "Previous" go back without re-querying
  // from the start, which a single "current cursor" cannot do: cursors only point forward.
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);

  const { data: keys } = useApiKeys();
  const query = useRequestLogs({
    ...filters,
    limit: PAGE_SIZE,
    ...(cursors[pageIndex] === undefined ? {} : { cursor: cursors[pageIndex] }),
  });

  /** Any filter change resets paging: a cursor from the previous ordering would skip rows. */
  function updateFilter(patch: Filters): void {
    setFilters((current) => {
      const next: Filters = { ...current, ...patch };
      // An empty selection removes the filter rather than sending a blank value, which the API
      // would reject as a malformed enum.
      for (const key of Object.keys(next) as (keyof Filters)[]) {
        if (next[key] === '') delete next[key];
      }
      return next;
    });
    setCursors([undefined]);
    setPageIndex(0);
  }

  const rows = query.data?.requests ?? [];
  const nextCursor = query.data?.nextCursor ?? null;

  function goNext(): void {
    if (nextCursor === null) return;
    setCursors((current) => {
      const next = current.slice(0, pageIndex + 1);
      next.push(nextCursor);
      return next;
    });
    setPageIndex((index) => index + 1);
  }

  return (
    <>
      <div>
        <h1 className="text-xl font-semibold">Request logs</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Every screenshot request in this workspace. Target hostnames are shown; full URLs and
          headers are never stored.
        </p>
      </div>

      <Card>
        <div className="flex flex-wrap gap-3">
          <label className="text-sm">
            <span className="block text-xs uppercase tracking-wide text-neutral-500">Status</span>
            <select
              data-testid="filter-status"
              className="mt-1 rounded border border-neutral-300 bg-white px-2 py-1 text-sm"
              value={filters.status ?? ''}
              onChange={(event) => updateFilter({ status: event.target.value })}
            >
              <option value="">All</option>
              <option value="succeeded">Succeeded</option>
              <option value="failed">Failed</option>
              <option value="rejected">Rejected</option>
              <option value="throttled">Throttled</option>
            </select>
          </label>

          <label className="text-sm">
            <span className="block text-xs uppercase tracking-wide text-neutral-500">Cache</span>
            <select
              data-testid="filter-cache"
              className="mt-1 rounded border border-neutral-300 bg-white px-2 py-1 text-sm"
              value={filters.cacheStatus ?? ''}
              onChange={(event) => updateFilter({ cacheStatus: event.target.value })}
            >
              <option value="">All</option>
              <option value="HIT">Hit</option>
              <option value="MISS">Miss</option>
              <option value="BYPASS">Bypass</option>
            </select>
          </label>

          <label className="text-sm">
            <span className="block text-xs uppercase tracking-wide text-neutral-500">API key</span>
            <select
              data-testid="filter-key"
              className="mt-1 rounded border border-neutral-300 bg-white px-2 py-1 text-sm"
              value={filters.keyId ?? ''}
              onChange={(event) => updateFilter({ keyId: event.target.value })}
            >
              <option value="">All</option>
              {(keys?.keys ?? []).map((key) => (
                <option key={key.id} value={key.id}>
                  {key.name}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm">
            <span className="block text-xs uppercase tracking-wide text-neutral-500">From</span>
            <input
              data-testid="filter-from"
              type="date"
              className="mt-1 rounded border border-neutral-300 bg-white px-2 py-1 text-sm"
              value={filters.from?.slice(0, 10) ?? ''}
              onChange={(event) =>
                updateFilter({
                  from:
                    event.target.value === ''
                      ? ''
                      : new Date(`${event.target.value}T00:00:00.000Z`).toISOString(),
                })
              }
            />
          </label>

          <label className="text-sm">
            <span className="block text-xs uppercase tracking-wide text-neutral-500">To</span>
            <input
              data-testid="filter-to"
              type="date"
              className="mt-1 rounded border border-neutral-300 bg-white px-2 py-1 text-sm"
              value={filters.to?.slice(0, 10) ?? ''}
              onChange={(event) =>
                updateFilter({
                  to:
                    event.target.value === ''
                      ? ''
                      : new Date(`${event.target.value}T23:59:59.999Z`).toISOString(),
                })
              }
            />
          </label>
        </div>
      </Card>

      <Card>
        {query.isError && (
          <p className="text-sm text-red-700" data-testid="request-logs-error">
            The request log could not be loaded.
          </p>
        )}

        {!query.isError && rows.length === 0 && !query.isLoading && (
          <p className="text-sm text-neutral-600" data-testid="request-logs-empty">
            No requests match these filters yet. Make a screenshot request and it will appear here
            within a few seconds.
          </p>
        )}

        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="request-logs-table">
              <thead className="text-xs uppercase tracking-wide text-neutral-500">
                <tr>
                  <th className="py-2 pr-4">When</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Target</th>
                  <th className="py-2 pr-4">Cache</th>
                  <th className="py-2 pr-4">Credits</th>
                  <th className="py-2 pr-4">Duration</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row: RequestLogSummary) => (
                  <tr key={row.requestId} className="border-t border-neutral-200">
                    <td className="py-2 pr-4 whitespace-nowrap">
                      {formatTimestamp(row.requestedAt)}
                    </td>
                    <td className="py-2 pr-4">
                      <StatusBadge status={row.status} />
                    </td>
                    <td className="py-2 pr-4">{row.targetHostname ?? '—'}</td>
                    <td className="py-2 pr-4">{row.cacheStatus}</td>
                    <td className="py-2 pr-4">{row.credits}</td>
                    <td className="py-2 pr-4 whitespace-nowrap">
                      {formatDuration(row.durationMs)}
                    </td>
                    <td className="py-2">
                      <Link
                        to="/request-logs/$requestId"
                        params={{ requestId: row.requestId }}
                        className="link text-sm"
                        data-testid={`request-log-link-${row.requestId}`}
                      >
                        Details
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 flex items-center gap-2">
          <Button
            variant="secondary"
            data-testid="request-logs-previous"
            disabled={pageIndex === 0}
            onClick={() => setPageIndex((index) => Math.max(0, index - 1))}
          >
            Previous
          </Button>
          <Button
            variant="secondary"
            data-testid="request-logs-next"
            disabled={nextCursor === null}
            onClick={goNext}
          >
            Next
          </Button>
          {query.isFetching && <span className="text-xs text-neutral-500">Loading…</span>}
        </div>
      </Card>
    </>
  );
}
